import {
  useSnackbar,
  WBFlex,
  WBTypography,
  WBBox,
  WBButton,
} from '@admiin-com/ds-web';
import {
  loadAirwallex,
  createElement,
  DropInElementOptions,
} from 'airwallex-payment-elements';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCustomerSecret as GET_CUSTOMER_SECRET,
  createPaymentIntentGuest as CREATE_GUEST_PAYMENT_INTENT,
  getPaymentGuest as GET_PAYMENT_GUEST,
  TaskStatus,
  PaymentStatus,
  TaskCategory,
  BillsPaymentInput,
} from '@admiin-com/ds-graphql';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useGuestTask } from '../../hooks/useGuestTask/useGuestTask';
import { useSearchParams } from 'react-router-dom';
import { POLLING_INTERVAL } from '../../constants/config';
import { airwallexTheme } from '@admiin-com/ds-design-token';
import BpayDetailsDialog from '../BpayDetailsDialog/BpayDetailsDialog';

const { VITE_AIRWALLEX_ENV } = import.meta.env;

let paymentIntent: any;
const AirwallexPaymentMethod: React.FC<{
  entityId?: string;
  isGuest: boolean;
  intentId?: string;
  paymentId?: string;
  taskId?: string;
  taskCategory?: TaskCategory | null;
  onClose: () => void;
  onSuccess: (e?: any) => void;
  onDownloadPdf?: () => void;
  billsPaymentInput?: BillsPaymentInput;
  onInitialized?: (initialized: boolean) => void;
  isFirmPaymentAuthorized?: boolean;
}> = ({
  entityId, //TODO: support hash for guest user
  intentId,
  paymentId,
  taskId,
  taskCategory,
  onSuccess,
  isGuest,
  onClose,
  onDownloadPdf,
  billsPaymentInput,
  onInitialized,
  isFirmPaymentAuthorized,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isSDKPaymentSuccess, setIsSDKPaymentSuccess] =
    useState<boolean>(false);
  const [initialized, setInitialized] = useState(false);
  const [showBpayDetails, setShowBpayDetails] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { taskGuest, refetch } = useGuestTask(token);
  // poll for status updates when using guest flow
  useEffect(() => {
    let interval: any;
    if (token) {
      interval = setInterval(() => {
        refetch();
      }, POLLING_INTERVAL);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [token, refetch]);
  const { data: paymentGuestData } = useQuery(gql(GET_PAYMENT_GUEST), {
    variables: {
      id: paymentId,
    },
    skip: !token || !paymentId,
    pollInterval: POLLING_INTERVAL,
  });
  const { data: getClientSecretData } = useQuery(gql(GET_CUSTOMER_SECRET), {
    variables: {
      entityId,
    },
    skip: isGuest || !entityId || !!paymentId,
    onError(err) {
      console.log(err);
    },
  });

  const [createPaymentIntentGuest] = useMutation(
    gql(CREATE_GUEST_PAYMENT_INTENT)
  );
  const showSnackbar = useSnackbar();
  const { t } = useTranslation();

  const initializeSDK = async () => {
    try {
      await loadAirwallex({
        env: VITE_AIRWALLEX_ENV,
        origin: window.location.origin,
      });

      setInitialized(true);
      if (onInitialized) {
        onInitialized(true);
      }
    } catch (e) {
      console.error(e);
      showSnackbar({
        message: t('initError', { ns: 'airwallex' }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log(
      'billsPaymentInputbillsPaymentInputbillsPaymentInput: ',
      billsPaymentInput
    );
  }, [billsPaymentInput]);

  const regeneratePaymentIntent = async () => {
    if (!isGuest) return;
    try {
      const res = await createPaymentIntentGuest({
        variables: {
          input: {
            entityId,
            taskId,
            paymentId: paymentId,
            billPayments: [billsPaymentInput],
          },
        },
      });
      if (res) {
        paymentIntent = JSON.parse(res.data.createPaymentIntentGuest);
        setInitialized(false);
        if (onInitialized) {
          onInitialized(false);
        }
        initializeSDK();
      }
    } catch (err) {
      console.error('regeneratePaymentIntent error', err);
    }
  };

  useEffect(() => {
    let pollingInterval: any;
    if (isSDKPaymentSuccess) {
      // Start polling only after payment has been submitted
      pollingInterval = setInterval(() => {
        if (
          taskGuest?.status === TaskStatus.COMPLETED ||
          taskGuest?.status === TaskStatus.SCHEDULED ||
          paymentGuestData?.getPaymentGuest?.status === PaymentStatus.SUCCEEDED
        ) {
          showSnackbar({
            message: t('paymentSuccess', { ns: 'airwallex' }),
            severity: 'success',
            horizontal: 'right',
            vertical: 'bottom',
          });
          onSuccess();
          setLoading(false);
          clearInterval(pollingInterval); // Stop polling once successful
        }
      }, POLLING_INTERVAL);
    }

    // Clear interval on cleanup or when isSDKPaymentSuccess changes
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [
    isSDKPaymentSuccess,
    taskGuest,
    paymentGuestData,
    t,
    onSuccess,
    showSnackbar,
  ]);

  useEffect(() => {
    if (isGuest) {
      (async () => {
        try {
          const res = await createPaymentIntentGuest({
            variables: {
              input: {
                entityId,
                taskId,
                paymentId: paymentId,
                billPayments: [billsPaymentInput],
              },
            },
          });
          if (res) {
            paymentIntent = JSON.parse(res.data.createPaymentIntentGuest);
            initializeSDK();
          }
        } catch (err) {
          console.error('createPaymentIntentGuest error', err);
        }
      })();
    } else if (getClientSecretData) {
      initializeSDK();
    } else if (paymentId && paymentIntent) {
      initializeSDK();
    } else {
      console.log('WAITING TO LOAD SDK...');
    }
  }, [
    isGuest,
    paymentId,
    getClientSecretData,
    billsPaymentInput?.paymentType,
    billsPaymentInput?.installments,
    billsPaymentInput?.paymentFrequency,
    billsPaymentInput?.scheduledAt,
    billsPaymentInput?.isUnlimitedRecurring,
  ]);

  useEffect(() => {
    let element: any;
    const createElementSDK = async () => {
      //TODO: should not be here?
      if (!entityId) {
        showSnackbar({
          message: 'Entity is required',
          severity: 'error',
          horizontal: 'right',
          vertical: 'bottom',
        });
        return;
      }

      let clientSecret;
      if (paymentIntent) {
        clientSecret = paymentIntent.client_secret;
      } else {
        const getCustomerSecret = JSON.parse(
          getClientSecretData.getCustomerSecret
        );
        clientSecret = getCustomerSecret?.client_secret;
      }

      let dropInPayload: DropInElementOptions = {
        client_secret: clientSecret, // customer client_secret or intent client_secret
        currency: 'AUD',
        mode: isGuest ? 'payment' : 'recurring',
        recurringOptions: {
          next_triggered_by: 'merchant',
          currency: 'AUD',
        },
        applePayRequestOptions: {
          countryCode: 'AU',
        },
        googlePayRequestOptions: {
          countryCode: 'AU',
        },
        theme: {
          //@ts-ignore
          airwallexTheme,
        },
        style: {
          base: {
            backgroundColor: 'transparent',
          },
        },
      };

      // If installments then use existing intent_id
      if (isGuest) {
        dropInPayload = {
          ...dropInPayload,
          intent_id: paymentIntent?.id || '',
        };
      }

      const element = createElement('dropIn', dropInPayload);

      const domElement = element?.mount('drop-in');
      domElement?.addEventListener('onSuccess', (event: any) => {
        console.log('onSuccess: ', event, token);
        setLoading(true);
        if (!token) {
          showSnackbar({
            message: t('paymentMethodSuccess', { ns: 'airwallex' }),
            severity: 'success',
            horizontal: 'right',
            vertical: 'bottom',
          });
          console.log(
            'SUBMITTED ',
            event?.detail.intent?.latest_payment_attempt?.payment_method
          );
          onSuccess(
            event?.detail.intent?.latest_payment_attempt?.payment_method
          );
          setLoading(false);
        } else {
          setIsSDKPaymentSuccess(true);
        }
      });
      domElement?.addEventListener('onError', (event: any) => {
        console.log('createElementSDK onError: ', event);
        const message = event?.detail?.error?.message || '';
        if (isGuest && /expired/i.test(message)) {
          showSnackbar({
            message: t('paymentIntentExpired', { ns: 'airwallex' }),
            severity: 'warning',
            horizontal: 'right',
            vertical: 'bottom',
          });
          regeneratePaymentIntent();
          return;
        }
        showSnackbar({
          message: message,
          severity: 'error',
          horizontal: 'right',
          vertical: 'bottom',
        });
        onClose();
      });
    };

    if (initialized) {
      createElementSDK();
    }

    return () => element?.destroy();
  }, [
    initialized,
    token,
    billsPaymentInput?.paymentType,
    billsPaymentInput?.installments,
    paymentIntent?.id,
  ]);

  return (
    <WBFlex flexDirection="column">
      {!initialized && loading ? (
        <Stack spacing={2}>
          <Typography variant="h4" textAlign="center" color="text.primary">
            {t('loadingPaymentForm', {
              ns: 'common',
            })}
          </Typography>
          <LinearProgress color="primary" />
        </Stack>
      ) : loading ? (
        <>
          <Typography variant="h4" textAlign="center" color="text.primary">
            Processing...
          </Typography>
          <LinearProgress color="primary" />
        </>
      ) : (
        <>
          {initialized && (
            <>
              <WBBox
                display={isGuest && !isFirmPaymentAuthorized ? 'none' : 'block'}
              >
                <div id="drop-in"></div>
              </WBBox>

              {isGuest && (
                <WBBox
                  display={
                    isGuest && !isFirmPaymentAuthorized ? 'none' : 'block'
                  }
                >
                  <WBTypography
                    variant="body2"
                    textAlign="center"
                    marginTop={2}
                    fontWeight={400}
                    color="grey"
                  >
                    Amount plus processing fee of 1.9% will be charged
                  </WBTypography>
                </WBBox>
              )}

              {initialized && taskCategory === TaskCategory.TAX && (
                <>
                  <WBFlex py={1} justifyContent="center" alignItems="center">
                    <WBBox
                      height="1px"
                      width="100%"
                      backgroundColor="#e8eaed"
                    />
                    <WBBox whiteSpace="nowrap">
                      <WBTypography color="#68707a" paddingX={1}>
                        {t('or', { ns: 'common' })}
                      </WBTypography>
                    </WBBox>

                    <WBBox
                      height="1px"
                      width="100%"
                      backgroundColor="#e8eaed"
                    />
                  </WBFlex>
                  <WBFlex
                    alignItems="center"
                    justifyContent="center"
                    width="100%"
                    sx={{ mb: { md: 5 } }}
                  >
                    <WBButton
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowBpayDetails(true);
                      }}
                    >
                      {t('viewBpayDetails', { ns: 'taskbox' })}
                    </WBButton>
                  </WBFlex>
                </>
              )}
            </>
          )}
        </>
      )}
      <BpayDetailsDialog
        isTax={taskCategory === TaskCategory.TAX}
        open={showBpayDetails}
        onClose={() => setShowBpayDetails(false)}
        reference={taskGuest?.futureLinkedTask?.bpayReferenceNumber ?? null}
        amount={taskGuest?.futureLinkedTask?.amount ?? null}
      />
    </WBFlex>
  );
};

export default AirwallexPaymentMethod;
